// src/pages/auth/RegisterPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Eye, 
  EyeOff, 
  Check,
  Info,
  AlertCircle,
  Loader2,
  Globe
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/RegisterPage.module.css';
import {
  register,
  resetRegisterState,
  selectRegistering,
  selectRegisterSuccess,
  selectRegisterError,
  selectUserLanguage,
  setLanguage
} from '../../store/slices/authSlice';
import {
  sendPin,
  resetPinState,
  clearPinErrors,
  selectPinSending,
  selectPinSendSuccess,
  selectPinSendError,
  selectSentToEmail,
  selectSentToPhone
} from '../../store/slices/pinCodeSlice';
import { tokenHelpers } from '../../store/api/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux selectors - Auth
  const isLoading = useSelector(selectRegistering);
  const registerSuccess = useSelector(selectRegisterSuccess);
  const registerError = useSelector(selectRegisterError);
  const reduxLanguage = useSelector(selectUserLanguage);
  
  // Redux selectors - PIN
  const isSendingPin = useSelector(selectPinSending);
  const pinSendSuccess = useSelector(selectPinSendSuccess);
  const pinSendError = useSelector(selectPinSendError);
  const sentToEmail = useSelector(selectSentToEmail);
  const sentToPhone = useSelector(selectSentToPhone);

  // Get language from context
  const { currentLanguage: contextLanguage } = useLanguage();
  
  // Use redux language if available, fallback to context language
  const currentLanguage = reduxLanguage || contextLanguage || 'ht';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    language: currentLanguage || 'ht',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage || 'ht');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Clean up on unmount
  useEffect(() => {
    dispatch(clearPinErrors());
    dispatch(resetPinState());
    setRegistrationAttempted(false);
    
    return () => {
      dispatch(resetRegisterState());
    };
  }, [dispatch]);

  // Handle PIN send errors
  useEffect(() => {
    if (pinSendError && registrationAttempted) {
      console.error('PIN send error:', pinSendError);
    }
  }, [pinSendError, registrationAttempted]);

  // ======================================================
  // TRANSLATIONS - ALL AT TOP LEVEL
  // ======================================================
  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: fillAllFields } = useTranslation('Please fill in all fields');
  const { translatedText: validEmail } = useTranslation('Please enter a valid email address');
  const { translatedText: validPhone } = useTranslation('Please enter a valid phone number');
  const { translatedText: passwordMinLength } = useTranslation('Password must be at least 6 characters');
  const { translatedText: passwordMismatch } = useTranslation('Passwords do not match');
  const { translatedText: selectLanguage } = useTranslation('Please select your preferred language');
  
  const { translatedText: registrationSuccess } = useTranslation('Registration Successful!');
  const { translatedText: registrationSuccessMessage } = useTranslation('Your account has been created successfully!');
  const { translatedText: verificationCodeSent } = useTranslation('A 6-digit verification code has been sent to your email and phone.');
  const { translatedText: enterVerificationCode } = useTranslation('Please check your email and phone for the verification code.');
  const { translatedText: pinSentViaEmail } = useTranslation('PIN sent via email');
  const { translatedText: pinSentViaPhone } = useTranslation('PIN sent via SMS');
  const { translatedText: pinSendFailed } = useTranslation('Failed to send PIN. Please try again.');
  const { translatedText: sendingPin } = useTranslation('Sending verification code...');
  const { translatedText: loginNow } = useTranslation('Login Now');
  const { translatedText: registrationFailed } = useTranslation('Registration Failed');
  const { translatedText: tryAgain } = useTranslation('Please try again');
  
  const { translatedText: fullName } = useTranslation('Full Name');
  const { translatedText: fullNamePlaceholder } = useTranslation('Enter your full name');
  const { translatedText: emailAddress } = useTranslation('Email Address');
  const { translatedText: emailPlaceholder } = useTranslation('Enter your email address');
  const { translatedText: phoneNumber } = useTranslation('Phone Number');
  const { translatedText: phonePlaceholder } = useTranslation('Enter your phone number');
  const { translatedText: passwordLabel } = useTranslation('Password');
  const { translatedText: passwordPlaceholder } = useTranslation('Create a password (min 6 characters)');
  const { translatedText: confirmPassword } = useTranslation('Confirm Password');
  const { translatedText: confirmPasswordPlaceholder } = useTranslation('Confirm your password');
  const { translatedText: registerButton } = useTranslation('Register');
  const { translatedText: creatingAccount } = useTranslation('Creating Account...');
  const { translatedText: providerRegistration } = useTranslation('Provider Registration');
  const { translatedText: infoBannerText } = useTranslation('Register as a service provider to offer your services to the Haitian community');
  const { translatedText: alreadyRegistered } = useTranslation('Already registered?');
  const { translatedText: signIn } = useTranslation('Sign In');

  // Language options
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ht', name: 'Kreyòl Ayisyen', flag: '🇭🇹' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const getLanguageName = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : code;
  };

  const getLanguageFlag = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.flag : '🌐';
  };

  const handleLanguageSelect = (languageCode) => {
    setSelectedLanguage(languageCode);
    setFormData({ ...formData, language: languageCode });
    setShowLanguageDropdown(false);
    // Update the global language context
    dispatch(setLanguage(languageCode));
  };

  const validateForm = () => {
    setError('');
    if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword) {
      setError(fillAllFields);
      return false;
    }
    if (!formData.email.includes('@')) {
      setError(validEmail);
      return false;
    }
    const phoneCleaned = formData.phone.replace(/\D/g, '');
    if (phoneCleaned.length < 8 || phoneCleaned.length > 15) {
      setError(validPhone);
      return false;
    }
    if (formData.password.length < 6) {
      setError(passwordMinLength);
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(passwordMismatch);
      return false;
    }
    if (!formData.language) {
      setError(selectLanguage);
      return false;
    }
    return true;
  };

  /**
   * Wait for token to be available in localStorage
   * The token is stored by AuthService.register() which is called by the register thunk
   */
  const waitForToken = (maxAttempts = 10, delay = 100) => {
    return new Promise((resolve) => {
      let attempts = 0;
      
      const checkToken = () => {
        const token = tokenHelpers.getAccessToken();
        attempts++;
        
        console.log(`[Register] 🔍 Token check attempt ${attempts}/${maxAttempts}:`, !!token);
        
        if (token) {
          console.log('[Register] ✅ Token found!');
          resolve(token);
        } else if (attempts >= maxAttempts) {
          console.warn('[Register] ⚠️ Token not found after maximum attempts');
          resolve(null);
        } else {
          setTimeout(checkToken, delay);
        }
      };
      
      checkToken();
    });
  };

  /**
   * Send PIN after successful registration
   * Uses the token that was automatically stored by AuthService during registration
   */
  const sendPinAfterRegistration = async (userId, userData) => {
    try {
      console.log('[Register] 📤 Sending PIN for user:', userId);
      
      // ✅ Wait for token to be available
      const token = await waitForToken();
      
      if (!token) {
        console.warn('[Register] ⚠️ No token found. Trying to get from Redux state...');
        // Try one more time to get token from localStorage directly
        const directToken = localStorage.getItem('access_token');
        if (directToken) {
          console.log('[Register] ✅ Token found via direct localStorage access');
        } else {
          console.error('[Register] ❌ No token available. PIN send will likely fail.');
          // But we'll still try - the interceptor will handle it
        }
      }
      
      // ✅ Call sendPin - the token is automatically added by axios interceptor
      const pinResult = await dispatch(sendPin()).unwrap();
      
      console.log('[Register] 📤 PIN send result:', pinResult);
      
      if (pinResult.success) {
        // Build delivery message
        let deliveryMessage = verificationCodeSent;
        const deliveryMethods = [];
        
        // Check if PIN was sent via email or SMS from the response
        const sentToEmailFlag = pinResult.data?.sentToEmail || sentToEmail;
        const sentToPhoneFlag = pinResult.data?.sentToPhone || sentToPhone;
        
        if (sentToEmailFlag) {
          deliveryMethods.push('📧 Email');
        }
        if (sentToPhoneFlag) {
          deliveryMethods.push('📱 SMS');
        }
        
        if (deliveryMethods.length > 0) {
          deliveryMessage = `A 6-digit verification code has been sent via ${deliveryMethods.join(' and ')}. Please check your ${deliveryMethods.join(' and ')}.`;
        }
        
        // Show success message with PIN verification
        const userConfirmed = window.confirm(
          `${registrationSuccess}\n\n✅ ${registrationSuccessMessage}\n\n${deliveryMessage}\n\n🔑 Please enter the 6-digit code to verify your account.\n\nWould you like to verify your account now?`
        );
        
        if (userConfirmed) {
          navigate('/verify-pin', { 
            state: { 
              email: formData.email,
              phone: formData.phone,
              fromRegister: true,
              userId: userId,
              userData: {
                name: formData.name,
                phone: formData.phone,
                language: formData.language || 'ht',
                role: 'service_provider',
              },
              autoSendPin: false // PIN already sent
            }
          });
        } else {
          alert(
            `${registrationSuccess}\n\n${registrationSuccessMessage}\n\n${verificationCodeSent}\n\nYou can verify your account later from your profile settings.`
          );
          navigate('/login');
        }
        return true;
      } else {
        // PIN send failed but registration succeeded
        console.error('[Register] PIN send failed:', pinResult);
        const userConfirmed = window.confirm(
          `${registrationSuccess}\n\n✅ ${registrationSuccessMessage}\n\n⚠️ We could not send the verification code. Would you like to try sending the verification code again?`
        );
        
        if (userConfirmed) {
          navigate('/verify-pin', { 
            state: { 
              email: formData.email,
              phone: formData.phone,
              fromRegister: true,
              userId: userId,
              userData: {
                name: formData.name,
                phone: formData.phone,
                language: formData.language || 'ht',
                role: 'service_provider',
              },
              autoSendPin: true // Resend PIN on load
            }
          });
        } else {
          alert(
            `${registrationSuccess}\n\n${registrationSuccessMessage}\n\nYou can verify your account later from your profile settings.`
          );
          navigate('/login');
        }
        return false;
      }
    } catch (pinError) {
      console.error('[Register] ❌ PIN sending error:', pinError);
      
      const userConfirmed = window.confirm(
        `${registrationSuccess}\n\n✅ ${registrationSuccessMessage}\n\n⚠️ We could not send the verification code. Would you like to try sending the verification code again?`
      );
      
      if (userConfirmed) {
        navigate('/verify-pin', { 
          state: { 
            email: formData.email,
            phone: formData.phone,
            fromRegister: true,
            userId: userId,
            userData: {
              name: formData.name,
              phone: formData.phone,
              language: formData.language || 'ht',
              role: 'service_provider',
            },
            autoSendPin: true
          }
        });
      } else {
        alert(
          `${registrationSuccess}\n\n${registrationSuccessMessage}\n\nYou can verify your account later from your profile settings.`
        );
        navigate('/login');
      }
      return false;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setRegistrationAttempted(true);
    setError('');

    const registrationData = {
      email: formData.email.trim(),
      password: formData.password,
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      language: formData.language || 'ht',
      role: 'service_provider',
    };

    try {
      console.log('[Register] 📝 Registering user:', { 
        email: registrationData.email, 
        name: registrationData.name,
        language: registrationData.language
      });
      
      // ✅ Register user - AuthService.register() automatically stores tokens in localStorage
      const result = await dispatch(register(registrationData)).unwrap();
      
      console.log('[Register] ✅ Registration successful:', result);
      
      if (result.success) {
        // Extract user data from result
        const userData = result.data?.user;
        const userId = userData?.id || userData?._id;
        
        if (!userId) {
          console.error('[Register] ❌ No userId found in registration response:', result);
          setError('Registration successful but user ID not found. Please try again.');
          setRegistrationAttempted(false);
          return;
        }
        
        console.log('[Register] ✅ User ID:', userId);
        console.log('[Register] ✅ User data:', userData);
        
        // ✅ The token is already stored by AuthService.register()
        // But we'll verify it's there and wait if needed
        
        // ✅ Now send PIN - the token should be available
        await sendPinAfterRegistration(userId, userData);
        
      } else {
        setError(result.error || tryAgain);
        setRegistrationAttempted(false);
      }
    } catch (err) {
      console.error('[Register] ❌ Registration error:', err);
      
      // Extract meaningful error message
      let errorMessage = registrationFailed;
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error) {
        errorMessage = err.error;
      }
      
      setError(errorMessage);
      setRegistrationAttempted(false);
    }
  };

  return (
    <div className={styles.registerContainer}>
      <div className={styles.registerWrapper}>
        <div className={styles.registerCard}>
          {/* Header */}
          <div className={styles.headerRow}>
            <button 
              className={styles.backArrow}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={24} color={colors.textPrimary} />
            </button>
            <div className={styles.headerContent}>
              <h4 className={styles.headerTitle}>{providerRegistration}</h4>
              <small className={styles.headerSubtitle}>
                {infoBannerText}
              </small>
            </div>
          </div>

          {/* Info Banner */}
          <div className={styles.infoBanner}>
            <Info size={20} color={colors.secondary} />
            <p className={styles.infoBannerText}>
              {infoBannerText}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.errorAlert}>
              <AlertCircle size={18} />
              <span className={styles.errorText}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleRegister}>
            {/* Full Name */}
            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{fullName}</label>
              <div className={styles.inputWrapper}>
                <User size={20} className={styles.inputIcon} color={colors.gray400} />
                <input
                  type="text"
                  className={styles.formControl}
                  placeholder={fullNamePlaceholder}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={isLoading || isSendingPin}
                />
              </div>
            </div>

            {/* Email */}
            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{emailAddress}</label>
              <div className={styles.inputWrapper}>
                <Mail size={20} className={styles.inputIcon} color={colors.gray400} />
                <input
                  type="email"
                  className={styles.formControl}
                  placeholder={emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  autoCapitalize="none"
                  disabled={isLoading || isSendingPin}
                />
              </div>
            </div>

            {/* Phone */}
            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{phoneNumber}</label>
              <div className={styles.inputWrapper}>
                <Phone size={20} className={styles.inputIcon} color={colors.gray400} />
                <input
                  type="tel"
                  className={styles.formControl}
                  placeholder={phonePlaceholder}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={isLoading || isSendingPin}
                />
              </div>
            </div>

            {/* Password */}
            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{passwordLabel}</label>
              <div className={styles.inputWrapper}>
                <Lock size={20} className={styles.inputIcon} color={colors.gray400} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`${styles.formControl} ${styles.formControlWithEye}`}
                  placeholder={passwordPlaceholder}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  disabled={isLoading || isSendingPin}
                />
                <button 
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading || isSendingPin}
                >
                  {showPassword ? <EyeOff size={20} color={colors.gray400} /> : <Eye size={20} color={colors.gray400} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{confirmPassword}</label>
              <div className={styles.inputWrapper}>
                <Lock size={20} className={styles.inputIcon} color={colors.gray400} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`${styles.formControl} ${styles.formControlWithEye}`}
                  placeholder={confirmPasswordPlaceholder}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  disabled={isLoading || isSendingPin}
                />
                <button 
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading || isSendingPin}
                >
                  {showConfirmPassword ? <EyeOff size={20} color={colors.gray400} /> : <Eye size={20} color={colors.gray400} />}
                </button>
              </div>
            </div>

            {/* Language Selector */}
            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{selectLanguage}</label>
              <div className={styles.languageSelectorWrapper}>
                <button
                  type="button"
                  className={styles.languageSelectorButton}
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  disabled={isLoading || isSendingPin}
                >
                  <Globe size={20} color={colors.gray400} />
                  <span className={styles.languageSelectorText}>
                    {getLanguageFlag(selectedLanguage)} {getLanguageName(selectedLanguage)}
                  </span>
                  <span className={styles.languageSelectorArrow}>
                    {showLanguageDropdown ? '▲' : '▼'}
                  </span>
                </button>
                
                {showLanguageDropdown && (
                  <div className={styles.languageDropdown}>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        className={`${styles.languageOption} ${selectedLanguage === lang.code ? styles.languageOptionActive : ''}`}
                        onClick={() => handleLanguageSelect(lang.code)}
                      >
                        <span className={styles.languageOptionFlag}>{lang.flag}</span>
                        <span className={styles.languageOptionName}>{lang.name}</span>
                        {selectedLanguage === lang.code && (
                          <Check size={16} color={colors.primary} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Register Button */}
            <button 
              type="submit"
              className={styles.registerButton}
              disabled={isLoading || isSendingPin}
            >
              {isLoading || isSendingPin ? (
                <>
                  <Loader2 size={20} className={styles.spinner} />
                  <span>{isLoading ? creatingAccount : sendingPin}</span>
                </>
              ) : (
                <>
                  <span>{registerButton}</span>
                  <Check size={20} />
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className={styles.loginLinkContainer}>
            <span className={styles.loginText}>{alreadyRegistered}</span>
            <Link to="/login" className={styles.loginLink}>
              {signIn}
            </Link>
          </div>

          {/* Haitian Flag Stripes */}
          <div className={styles.flagContainer}>
            <div className={`${styles.flagStripe} ${styles.flagBlue}`} />
            <div className={`${styles.flagStripe} ${styles.flagRed}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;